import { SignAlgorithm, EnumUtils } from "../../utils/enums";

export interface Asset {
  readonly address: string;
  readonly symbol: string;
  readonly algo: SignAlgorithm;
}

export const AssetUtils = {
  create({
    address,
    symbol,
    algo,
  }: {
    address: string;
    symbol: string;
    algo: SignAlgorithm;
  }): Asset {
    return {
      address,
      symbol,
      algo,
    };
  },

  empty(): Asset {
    return AssetUtils.create({
      address: "",
      symbol: "",
      algo: SignAlgorithm.Error,
    });
  },

  fromMap(map: Record<string, any>): Asset {
    try {
      const algoValue =
        map["algo"] != null && map["algo"] !== "" ? String(map["algo"]).toUpperCase() : null;
      // Parse algoValue using EnumUtils and cast to SignAlgorithm
      const parsedAlgoString = algoValue
        ? EnumUtils.enumFromString(SignAlgorithm as unknown as Record<string, string>, algoValue)
        : undefined;
      // Ensure parsedAlgo is a valid SignAlgorithm value
      const parsedAlgo =
        parsedAlgoString && Object.values(SignAlgorithm).includes(parsedAlgoString as SignAlgorithm)
          ? (parsedAlgoString as SignAlgorithm)
          : SignAlgorithm.Error;

      return AssetUtils.create({
        address: String(map["address"] ?? ""),
        symbol: map["symbol"] != null && map["symbol"] !== "" ? String(map["symbol"]) : "Error",
        algo: parsedAlgo,
      });
    } catch (e) {
      console.error(String(e));
      return AssetUtils.empty();
    }
  },

  toMap(asset: Asset): Record<string, any> {
    return {
      address: asset.address,
      symbol: asset.symbol,
      algo: asset.algo, // Using enum value directly; adjust to asset.algo.toString() if string name is needed
    };
  },

  toJson(asset: Asset): string {
    return JSON.stringify(AssetUtils.toMap(asset));
  },
  fromJson(source: string): Asset {
    return AssetUtils.fromMap(JSON.parse(source));
  },
  copyWith({
    asset,
    address,
    symbol,
    algo,
  }: {
    asset: Asset;
    address?: string;
    symbol?: string;
    algo?: SignAlgorithm;
  }): Asset {
    return AssetUtils.create({
      address: address ?? asset.address,
      symbol: symbol ?? asset.symbol,
      algo: algo ?? asset.algo,
    });
  },
  toString(asset: Asset): string {
    return `Asset(address: ${asset.address}, symbol: ${asset.symbol}, algo: ${asset.algo})`;
  },
  equals(asset1: Asset, asset2: Asset): boolean {
    if (asset1 === asset2) return true;
    return (
      asset1.address === asset2.address &&
      asset1.symbol === asset2.symbol &&
      asset1.algo === asset2.algo
    );
  },
};
